import { getActiveClipAtMs, getOverlayTrack, mapEffectiveMsToSourceMs } from '../timelineModel.js'
import { renderClipFrame, renderOverlayLayer } from '../compositor/canvasCompositor.js'
import { OfflineOverlayRenderer } from '../compositor/overlayRenderer.js'

const loadImageEl = (url) => new Promise((resolve, reject) => {
    const el = document.createElement('img')
    el.crossOrigin = 'anonymous'
    el.onload = () => resolve(el)
    el.onerror = reject
    el.src = url
})

// A document switched from video to photo mode (see MediaEditor.vue's toggleDocumentMode) still
// has a video clip as its source - loading that URL into an <img> (as this used to, unconditionally)
// simply fails to decode, since browsers don't render video files through <img>. A <video> element
// only actually presents a frame once seeked, and a bare `el.currentTime = x` isn't synchronous, so
// this waits for 'seeked' - with a hard timeout fallback, same reasoning as exportTimeline.js's own
// hardSeekTo: a freshly created element's very first seek can fail to ever fire it in some browsers.
const loadVideoFrameEl = (url, atSeconds) => new Promise((resolve, reject) => {
    const el = document.createElement('video')
    el.crossOrigin = 'anonymous'
    el.muted = true
    el.playsInline = true
    el.preload = 'auto'
    el.onerror = reject
    el.onloadeddata = () => {
        let done = false
        const finish = () => {
            if (done) return
            done = true
            el.removeEventListener('seeked', finish)
            clearTimeout(timeoutId)
            resolve(el)
        }
        el.addEventListener('seeked', finish)
        el.currentTime = atSeconds
        const timeoutId = setTimeout(finish, 800)
    }
    el.src = url
})

// Photo mode's export counterpart to exportTimeline.js/mediaRecorderFallback.js - renders a
// single instant instead of looping frames into an encoder, reusing the exact same compositing
// building blocks (renderClipFrame + OfflineOverlayRenderer) those already use per-frame.
export const exportTimelineToImage = async (timeline, { resolveSource, atMs = 0 }) => {
    const canvasSize = { width: timeline.canvas.width, height: timeline.canvas.height }
    const canvas = document.createElement('canvas')
    canvas.width = canvasSize.width
    canvas.height = canvasSize.height
    const ctx = canvas.getContext('2d')

    const background = { mode: timeline.canvas.backgroundMode, color: timeline.canvas.backgroundColor }
    const clip = getActiveClipAtMs(timeline, atMs)
    if (clip) {
        const source = resolveSource(clip.fileId)
        const mediaEl = source.kind === 'video'
            ? await loadVideoFrameEl(source.url, mapEffectiveMsToSourceMs(clip, atMs - clip.timelineStartMs) / 1000)
            : await loadImageEl(source.url)
        renderClipFrame(ctx, mediaEl, canvasSize, clip, { background })
    }

    const overlayTrack = getOverlayTrack(timeline)
    if (overlayTrack && overlayTrack.overlays.length) {
        const offline = new OfflineOverlayRenderer(canvasSize)
        await offline.prepare(overlayTrack.overlays)
        renderOverlayLayer(ctx, offline.renderAt(overlayTrack.overlays, atMs), canvasSize)
        offline.dispose()
    }

    return new Promise((resolve) => canvas.toBlob(resolve, 'image/png'))
}
